// Release 26, before anything else: on the old address a device holding no SATU data leaves at
// once for the same page on the new address (./app/pindahAwal); then nothing below starts.
import { SEDANG_PINDAH } from './app/pindahAwal'
import './styles/globals.css'
import './app/splash/splash.css'
// Before the router reads the address: an e-mail link's single-use hash (the query) or Supabase
// session (the fragment) leaves the address; what its page needs is kept in memory for it.
import './app/buangFragmenSesi'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router/dom'
import { pasangPenahanTransisi } from './app/batasTransisi'
import { getDb } from './app/db'
import { tangkapTawaran } from './app/pasang/tawaran'
import { laporkanGalat, mulaiPelaporan } from './app/pelaporan'
import { router } from './app/router'
import { sapuSisaBacaPeramban } from './app/sisaBaca'
import { runGate, SPLASH_MIN_MS, SPLASH_START_MARK } from './app/splash/gate'
import { settleSplash, updateSplashInstall } from './app/splash/splash'
import { useSesi } from './app/store/sesi'
import { pasangTahanKirimAsli } from './app/tahanKirimAsli'
import { startSession } from './auth/app'

// Legacy storage name from SIMU; kept so devices keep their data (pinned: test/nama-warisan.test.ts).
const PETUNJUK_KEY = 'simu:sesi-petunjuk'
function bacaPetunjuk(): string | null {
  try {
    return localStorage.getItem(PETUNJUK_KEY)
  } catch {
    return null
  }
}

// The splash settles once its target mark (the header's or /masuk's) is in the DOM, so the
// FLIP has somewhere to land; bounded to about twenty frames.
async function markReady(): Promise<void> {
  for (let i = 0; i < 20 && document.querySelector('[data-mark-target]') === null; i++) {
    await new Promise((resolve) => requestAnimationFrame(() => resolve(undefined)))
  }
}

/** The app itself: everything a launch starts, unless the page is leaving (release 26). */
function mulai(): void {
  // Before anything renders: a form's submit that React misses (an Enter held while a route
  // change runs in a view transition) must never become the browser's own submission, which
  // would reload the page with the form's fields, a NIK or a PIN, in the address.
  pasangTahanKirimAsli(window)
  // A view transition the browser aborted for a slow DOM update only skips its animation; its
  // rejection, which React leaves unhandled, is no error (release 14). Every other one passes.
  pasangPenahanTransisi(window)
  // Release 27: what the removed photo reading kept in the browser's storage goes (./app/sisaBaca).
  sapuSisaBacaPeramban()
  // Release 33: the browser's install offer comes early, often before React mounts; it is held
  // for Pasang SATU (Akun, the shell's band), and the install asks for persistent storage.
  tangkapTawaran(window)

  const container = document.getElementById('root')
  if (container === null) {
    throw new Error('SATU: #root element missing from index.html')
  }

  // The session lives in the device database, so reading it waits for the worker and the
  // SQLite wasm, which a first install over a slow link has not cached yet. The last known
  // state is also kept in localStorage. A device that was signed out, or has never signed in,
  // paints the sign-in form first and opens the database once the browser is idle; a locked
  // or active device opens it at once, because the PIN screen and Beranda need it.
  useSesi.subscribe((s) => {
    if (s.status === 'memuat') return
    try {
      localStorage.setItem(PETUNJUK_KEY, s.status)
    } catch {
      // private window or blocked storage: the next launch simply waits for the database
    }
  })
  const tanpaSesi = (bacaPetunjuk() ?? 'tanpa_sesi') === 'tanpa_sesi'

  const startedAt = performance.getEntriesByName(SPLASH_START_MARK)[0]?.startTime

  if (tanpaSesi) {
    useSesi.setState({ status: 'tanpa_sesi' })
    const t0 = startedAt ?? performance.now()
    void new Promise((resolve) =>
      setTimeout(resolve, Math.max(0, SPLASH_MIN_MS - (performance.now() - t0))),
    )
      .then(markReady)
      .then(() => {
        settleSplash({ reason: 'masuk', elapsedMs: performance.now() - t0 })
        // The worker, the wasm and the session start behind the painted form; signing in
        // waits for them anyway, and typing an email and a password covers the time.
        const idle = window.requestIdleCallback ?? ((fn: () => void) => window.setTimeout(fn, 200))
        idle(() => void startSession())
      })
  } else {
    // One database client for the whole tab, opened before React mounts so the worker boots
    // while the shell renders beneath the splash. The gate releases the splash when the
    // worker and the cached aggregates are ready (400 ms minimum, 1500 ms cap), and the
    // offline identity (section 05) reads sesi_lokal, sets the sync scope and hands the worker
    // its token provider.
    const db = getDb()
    const sessionReady = startSession()
    const gate = runGate({
      client: db,
      ...(startedAt === undefined ? {} : { startedAt }),
      onInstall: updateSplashInstall,
    })
    void Promise.all([gate, sessionReady.then(markReady)]).then(([result]) => settleSplash(result))
  }

  // Error reporting (section 22): only in a build made with VITE_SENTRY_DSN, loaded after the
  // first paint (./app/pelaporan). Errors an error boundary caught (a screen's route boundary)
  // never reach window.onerror, so the root hands them over too, still logging them as
  // React does by default.
  const SENTRY_DSN: string | undefined = import.meta.env.VITE_SENTRY_DSN || undefined
  // Guarded here, not only inside: a build without a DSN folds this away, and with it the
  // lazy chunk, which the service worker would otherwise precache for nothing.
  if (SENTRY_DSN !== undefined) mulaiPelaporan(SENTRY_DSN)

  createRoot(
    container,
    SENTRY_DSN === undefined
      ? {}
      : {
          onCaughtError: (error, info) => {
            console.error(error)
            laporkanGalat(error, info.componentStack)
          },
        },
  ).render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>,
  )

  // Development seeding helpers (window.__satuDev), only in the development and `seed`
  // build modes: a production build drops this branch and never emits the chunk.
  if (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'seed') {
    void import('./dev/seed').then((m) => m.installDevTools())
  }
}

if (!SEDANG_PINDAH) mulai()
